import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle, XCircle } from "@phosphor-icons/react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const r = await http.get(`/billing/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          await checkAuth();
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (e) {
        console.debug(`billing poll attempt ${attempt} failed, retrying:`, e?.message || e);
      }
      if (attempt >= 8) {
        setStatus("timeout");
        return;
      }
      setTries(attempt + 1);
      setTimeout(() => poll(attempt + 1), 2500);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, checkAuth]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-6">
      <div className="bg-[#121212] border border-zinc-800 p-10 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <div className="overline mb-3">Verifying Payment · attempt {tries + 1}/9</div>
            <div className="h-1 mm-scan-bar mb-4" />
            <div className="text-zinc-400 text-sm">Confirming with Stripe…</div>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={48} className="text-[#39FF14] mx-auto mb-4" weight="fill" />
            <div className="heading text-2xl font-black uppercase mb-2">Ready for Battle</div>
            <div className="text-zinc-400 text-sm mb-6">Your subscription is active. Full WarPaint Rule Engine unlocked.</div>
            <button data-testid="enter-app" onClick={() => nav("/app")} className="w-full py-3 bg-[#39FF14] text-black uppercase tracking-widest text-xs font-black hover:bg-[#2ee30d]">
              Enter the War Room →
            </button>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <XCircle size={48} className="text-[#FF3B30] mx-auto mb-4" weight="fill" />
            <div className="heading text-2xl font-black uppercase mb-2">
              {status === "expired" ? "Session Expired" : status === "timeout" ? "Timed Out" : "Something Went Wrong"}
            </div>
            <div className="text-zinc-400 text-sm mb-6">No charge was completed. Try again or reach out to support.</div>
            <Link to="/pricing" className="block py-3 bg-[#39FF14] text-black uppercase tracking-widest text-xs font-black hover:bg-[#2ee30d]">
              Back to Pricing
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
